<script setup lang="ts">
type DataRequestData = App.DataRequests.Data.DataRequestData;
type EnumData = App.Base.Data.EnumData;
type PartialStudentScholarshipData = App.Students.Data.PartialStudentScholarshipData;
type StudentScholarshipData = App.Students.Data.StudentScholarshipData;

import DataRequestDialog from '@/domains/data-request/components/DataRequestDialog.vue';
import ScholarshipDataRequestForm from '@/domains/data-request/components/student/scholarships/ScholarshipDataRequestForm.vue';

const props = defineProps<{
  scholarship: StudentScholarshipData;
  types: EnumData[];
  statuses: EnumData[];
  dataRequest?: DataRequestData;
}>();

const dataRequestAttributes = computed(
  () => props.dataRequest?.attributes as PartialStudentScholarshipData | undefined
);

const formUrl = computed(() => {
  if (props.dataRequest?.status.value === 'pending') {
    return route('student.data-requests.scholarship.update', {
      dataRequest: props.dataRequest.id
    });
  }
  return route('student.data-requests.scholarship.store');
});

const form = useForm({
  url: formUrl.value,
  method: 'POST',
  preserveState: false,
  fields: {
    scholarship: {
      scholarship_name: (() => {
        const name =
          dataRequestAttributes.value?.scholarship_name || props.scholarship?.scholarship?.name;
        return name ? { label: name, value: name } : null;
      })(),
      type: dataRequestAttributes.value?.type?.value || props.scholarship?.type?.value || null,
      institute_id:
        buildSelectableInstitute(
          dataRequestAttributes.value?.institute || props.scholarship?.institute
        ) ?? null,
      status: dataRequestAttributes.value?.status || props.scholarship?.status,
      amount: dataRequestAttributes.value?.amount || props.scholarship?.amount,
      can_renew: (() => {
        const renewableYears =
          dataRequestAttributes.value?.years_renewable || props.scholarship?.years_renewable;
        return renewableYears ? renewableYears > 1 : false;
      })(),
      years_renewable:
        dataRequestAttributes.value?.years_renewable || props.scholarship?.years_renewable
    },
    user_comment: props.dataRequest?.user_comment ?? ''
  },
  transform: (fields) => ({
    ...fields,
    requestable_id: props.scholarship?.id,
    scholarship: {
      scholarship_name: fields.scholarship.scholarship_name?.label,
      type: fields.scholarship.type,
      institute_id: fields.scholarship.institute_id?.value,
      status: fields.scholarship.status?.value,
      amount: fields.scholarship.amount,
      years_renewable: fields.scholarship.years_renewable
    }
  })
});
</script>

<template>
  <DataRequestDialog dataRequestType="Scholarships" :form="form">
    <template #form>
      <ScholarshipDataRequestForm
        :dataRequest="dataRequest"
        :form="form"
        :scholarship="scholarship"
        :statuses="statuses"
        :types="types"
      />
    </template>
  </DataRequestDialog>
</template>
